import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { RotateCcw, Download, ArrowRight, Check, Hand, ChevronsDown, ZoomIn, ArrowLeftRight, CornerUpLeft, Plus } from "lucide-react";
import { useAdminImages, ChangeBtn } from "./AboutPremium";
import { CONTACT } from "./siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imgUrl = (id) => `${API}/site/fabric-images/${id}`;
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const FOIL = "linear-gradient(100deg,#8B6B3A 0%,#E9D3A1 25%,#B89B72 45%,#F4E4BC 60%,#8B6B3A 100%)";
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

// [key, label, twoPiece]. Slot = tf_<who>_<key> (pose 1), tf_<who>_<key>_p2 … for more poses.
const STYLES = {
  women: [["maxi", "Maxi"], ["peplum", "Peplum Top", true], ["kurti", "Kurti"], ["anarkali", "Anarkali"], ["coord", "Co-ord Set", true], ["gown", "Gown"]],
  men: [["kurta", "Kurta"], ["shirt", "Shirt", true]],
  boy: [["kurta", "Kurta"], ["shirt", "Shirt", true]],
  girl: [["frock", "Frock"], ["lehenga", "Lehenga"]],
};
const MODELS = [["women", "Women"], ["men", "Men"], ["kids", "Kids"]];
const POSES = [1, 2, 3, 4];
const TARGETS = [["all", "Full outfit"], ["top", "Top"], ["bottom", "Bottom"]];
const EMPTY = { top: { fabric: null, pattern: null }, bottom: { fabric: null, pattern: null } };

const CSS = `
@keyframes ts-up{from{transform:translateY(0)}to{transform:translateY(-50%)}}
.ts-auto{animation:ts-up 40s linear infinite}.ts-rail:hover .ts-auto{animation-play-state:paused}
.ts-fade:before,.ts-fade:after{content:"";position:absolute;left:0;right:0;height:56px;z-index:2;pointer-events:none}
.ts-fade:before{top:0;background:linear-gradient(#FBF8F2,transparent)}.ts-fade:after{bottom:0;background:linear-gradient(transparent,#FBF8F2)}
.ts-scroll::-webkit-scrollbar{width:4px}.ts-scroll::-webkit-scrollbar-thumb{background:#C9A96E;border-radius:4px}
@keyframes ts-shim{0%{background-position:0% 50%}100%{background-position:200% 50%}}
.ts-foil{background:${FOIL};background-size:200% 100%;-webkit-background-clip:text;background-clip:text;color:transparent;animation:ts-shim 4s linear infinite}
`;

const loadImg = async (src) => {
  // fetch as a blob so a cached no-CORS copy of the image can't taint the canvas
  const r = await fetch(src + (src.includes("?") ? "&" : "?") + "cors=1", { mode: "cors", cache: "no-store" });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const url = URL.createObjectURL(await r.blob());
  return new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = () => rej(new Error("decode failed")); i.src = url; });
};

// Pick the part of a fabric photo that is really cloth (skips black/white studio background, mannequin edges).
function pickCloth(img) {
  const iw = img.naturalWidth, ih = img.naturalHeight, N = 48;
  const c = document.createElement("canvas"); c.width = N; c.height = N;
  const x = c.getContext("2d", { willReadFrequently: true });
  x.drawImage(img, 0, 0, N, N);
  const d = x.getImageData(0, 0, N, N).data;
  const px = (i, j) => { const k = (j * N + i) * 4; return [d[k], d[k + 1], d[k + 2]]; };
  const edge = [];
  for (let i = 0; i < N; i++) edge.push(px(i, 0), px(i, N - 1), px(0, i), px(N - 1, i));
  const med = [0, 1, 2].map((ch) => edge.map((q) => q[ch]).sort((a, b) => a - b)[edge.length >> 1]);
  const bg = new Uint8Array(N * N); let fg = 0;
  for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
    const q = px(i, j); const b = Math.hypot(q[0] - med[0], q[1] - med[1], q[2] - med[2]) < 45 ? 1 : 0;
    bg[j * N + i] = b; if (!b) fg++;
  }
  const frac = fg / (N * N);
  if (frac > 0.97) return [0, 0, iw, ih];                                  // all cloth
  if (frac < 0.2) return [iw * 0.15, ih * 0.15, iw * 0.7, ih * 0.7];     // flat plain swatch
  const w = Math.round(N * 0.3); let best = null;                          // product photo: find a pure-cloth window
  for (let j = 0; j + w <= N; j += 2) for (let i = 0; i + w <= N; i += 2) {
    let n = 0; for (let jj = j; jj < j + w; jj++) for (let ii = i; ii < i + w; ii++) if (!bg[jj * N + ii]) n++;
    const score = n / (w * w), dist = Math.hypot(i + w / 2 - N / 2, j + w / 2 - N / 2);
    const rank = (score >= 0.92 ? 10 : score) - dist / 200;
    if (!best || rank > best.rank) best = { rank, i, j };
  }
  return [(best.i / N) * iw, (best.j / N) * ih, (w / N) * iw, (w / N) * ih];
}

// Fallback (when no server mask exists): bright low-saturation pixels connected to the body centre.
function garmentMask(data, w, h) {
  const n = w * h, ok = new Uint8Array(n), m = new Uint8Array(n);
  for (let p = 0, i = 0; p < n; p++, i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2];
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    ok[p] = mn > 110 && mx - mn < 14 ? 1 : 0;
  }
  const stack = [];
  [0.35, 0.5, 0.6, 0.72].forEach((fy) => { const p = Math.floor(h * fy) * w + Math.floor(w / 2); if (ok[p]) stack.push(p); });
  while (stack.length) {
    const p = stack.pop(); if (m[p]) continue; m[p] = 1;
    const x = p % w;
    if (x > 0 && ok[p - 1] && !m[p - 1]) stack.push(p - 1);
    if (x < w - 1 && ok[p + 1] && !m[p + 1]) stack.push(p + 1);
    if (p >= w && ok[p - w] && !m[p - w]) stack.push(p - w);
    if (p < n - w && ok[p + w] && !m[p + w]) stack.push(p + w);
  }
  return m;
}

export default function TryStudio() {
  const { isAdmin, src, reload } = useAdminImages();
  const [model, setModel] = useState("women");
  const [kid, setKid] = useState("girl");
  const who = model === "kids" ? kid : model;
  const [style, setStyle] = useState(STYLES.women[0][0]);
  const [pose, setPose] = useState(1);
  const [fabrics, setFabrics] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [sel, setSel] = useState(EMPTY);
  const [target, setTarget] = useState("all");
  const [recent, setRecent] = useState([]);
  const [over, setOver] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [flip, setFlip] = useState(false);
  const [hasBottom, setHasBottom] = useState(false);
  const [err, setErr] = useState("");
  const canvasRef = useRef(null);
  const history = useRef([]);
  const tiles = useRef(new Map());
  const base = useRef({ key: "", img: null, mk: null, refs: {} });

  useEffect(() => {
    axios.get(`${API}/site/fabrics`, { params: { kind: "fabric", limit: 200 } }).then((r) => setFabrics(r.data || [])).catch(() => {});
    axios.get(`${API}/site/fabrics`, { params: { kind: "pattern", limit: 200 } }).then((r) => setPatterns(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { setStyle(STYLES[who][0][0]); setPose(1); setSel(EMPTY); setRecent([]); history.current = []; }, [who]);
  useEffect(() => { setPose(1); }, [style]);

  const styleRow = STYLES[who].find((s) => s[0] === style) || STYLES[who][0];
  const styleName = styleRow[1];
  const slotFor = (st, n) => (n === 1 ? `tf_${who}_${st}` : `tf_${who}_${st}_p${n}`);
  const slot = slotFor(style, pose);

  // which photo to show: this pose → this style → whole-model photo → any style of this model
  const photoSlot = useMemo(() => {
    const chain = pose === 1 ? [slot, `tf_${who}`, ...STYLES[who].map((s) => `tf_${who}_${s[0]}`)] : [slot];
    return chain.find((s) => src(s, null)) || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot, who, pose, src]);
  const photo = photoSlot ? src(photoSlot, null) : null;
  const maskUrl = photoSlot ? src(`${photoSlot}_mask`, null) : null;
  const poseHas = (n) => !!src(slotFor(style, n), null);
  const thumbOf = (k) => src(`tf_${k}`, null) || (STYLES[k] || []).map((s) => src(`tf_${k}_${s[0]}`, null)).find(Boolean) || null;

  const picks = target === "all"
    ? ["top", "bottom"]
    : [target];
  const cur = sel[picks[0]];
  const anyFabric = [sel.top, sel.bottom].some((r) => r.fabric || r.pattern);

  const commit = (next) => { history.current.push(sel); setSel(next); };
  const apply = (type, item) => {
    if (!item) return;
    const next = { top: { ...sel.top }, bottom: { ...sel.bottom } };
    picks.forEach((r) => { next[r][type] = item; });
    commit(next);
    setRecent((r) => [{ type, item }, ...r.filter((x) => x.item.id !== item.id)].slice(0, 3));
  };
  const undo = () => { const p = history.current.pop(); if (p) setSel(p); };
  const reset = () => { if (anyFabric) commit(EMPTY); };

  const getTile = async (item, size) => {
    const key = `${item.id}:${size}`;
    if (tiles.current.has(key)) return tiles.current.get(key);
    const t = await loadImg(imgUrl(item.images?.[0]));
    const [sx, sy, sw, sh] = pickCloth(t);
    const s = document.createElement("canvas"); s.width = size; s.height = Math.max(1, Math.round(size * sh / sw));
    s.getContext("2d").drawImage(t, sx, sy, sw, sh, 0, 0, s.width, s.height);
    tiles.current.set(key, s);
    return s;
  };

  const render = useCallback(async () => {
    const cv = canvasRef.current; if (!cv || !photo) return;
    try {
      setErr("");
      const key = `${photo}|${maskUrl}`;
      if (base.current.key !== key) {
        const img = await loadImg(photo);
        let W, H, mk;
        if (maskUrl) {
          try {
            const mi = await loadImg(maskUrl);
            W = mi.naturalWidth; H = mi.naturalHeight;
            const mc = document.createElement("canvas"); mc.width = W; mc.height = H;
            const mctx = mc.getContext("2d", { willReadFrequently: true });
            mctx.drawImage(mi, 0, 0);
            const d = mctx.getImageData(0, 0, W, H).data;
            const top = new Uint8Array(W * H), bot = new Uint8Array(W * H);
            let nb = 0;
            for (let p = 0, i = 0; p < top.length; p++, i += 4) { top[p] = d[i] > 127 ? 1 : 0; if (d[i + 1] > 127) { bot[p] = 1; nb++; } }
            mk = { top, bottom: bot, two: nb > W * H * 0.01 };
          } catch { mk = null; }
        }
        if (!mk) {
          H = Math.min(1100, img.naturalHeight); W = Math.round(img.naturalWidth * H / img.naturalHeight);
          cv.width = W; cv.height = H;
          const c0 = cv.getContext("2d", { willReadFrequently: true });
          c0.drawImage(img, 0, 0, W, H);
          const m = garmentMask(c0.getImageData(0, 0, W, H).data, W, H);
          mk = { top: m, bottom: m, two: false };
        }
        cv.width = W; cv.height = H;
        const ctx = cv.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, W, H);
        const o = ctx.getImageData(0, 0, W, H).data;
        const refs = {};
        ["top", "bottom"].forEach((r) => {
          const lums = []; const m = mk[r];
          for (let p = 0; p < m.length; p += 7) if (m[p]) lums.push((o[p * 4] + o[p * 4 + 1] + o[p * 4 + 2]) / 3);
          lums.sort((a, b) => a - b);
          refs[r] = lums[Math.floor(lums.length * 0.8)] || 235;
        });
        base.current = { key, img, mk, refs, orig: o, W, H };
        setHasBottom(mk.two);
      }
      const { img, mk, refs, orig, W, H } = base.current;
      const ctx = cv.getContext("2d");
      ctx.globalCompositeOperation = "source-over"; ctx.filter = "none";
      ctx.drawImage(img, 0, 0, W, H);
      const hasBottomMask = mk.bottom.some(Boolean);

      for (const r of ["top", "bottom"]) {
        const s = sel[r];
        if (!s.fabric && !s.pattern) continue;
        if (r === "bottom" && !hasBottomMask) continue;
        const tex = document.createElement("canvas"); tex.width = W; tex.height = H;
        const tc = tex.getContext("2d", { willReadFrequently: true });
        tc.fillStyle = "#ffffff"; tc.fillRect(0, 0, W, H);
        if (s.fabric) { tc.globalCompositeOperation = "source-over"; tc.fillStyle = tc.createPattern(await getTile(s.fabric, Math.round(W * 0.35)), "repeat"); tc.fillRect(0, 0, W, H); }
        if (s.pattern) { tc.globalCompositeOperation = s.fabric ? "multiply" : "source-over"; tc.fillStyle = tc.createPattern(await getTile(s.pattern, Math.round(W * 0.32)), "repeat"); tc.fillRect(0, 0, W, H); }
        const td = tc.getImageData(0, 0, W, H).data;

        // NATURAL GARMENT REPLACEMENT
        // Keep the original garment's lighting, folds, shadows
        // and body structure, but replace its actual colour
        // with the selected fabric/pattern.

        const layer = document.createElement("canvas");
        layer.width = W;
        layer.height = H;

        const lc = layer.getContext("2d", {
          willReadFrequently: true
        });

        const out = lc.createImageData(W, H);
        const od = out.data;
        const m = mk[r];

        // Build a stable luminance reference from the original
        // garment. This preserves realistic garment structure.
        const garmentLuma = [];

        for (let p = 0, i = 0; p < m.length; p++, i += 4) {
          if (!m[p]) continue;

          garmentLuma.push(
            0.2126 * orig[i] +
            0.7152 * orig[i + 1] +
            0.0722 * orig[i + 2]
          );
        }

        garmentLuma.sort((a, b) => a - b);

        const lowLuma =
          garmentLuma[Math.floor(garmentLuma.length * 0.08)] || 70;

        const highLuma =
          garmentLuma[Math.floor(garmentLuma.length * 0.92)] || 220;

        const lumaRange = Math.max(
          40,
          highLuma - lowLuma
        );

        for (let p = 0, i = 0; p < m.length; p++, i += 4) {

          if (!m[p]) continue;

          const originalLuma =
            0.2126 * orig[i] +
            0.7152 * orig[i + 1] +
            0.0722 * orig[i + 2];

          // Normalize original garment lighting.
          let light =
            (originalLuma - lowLuma) / lumaRange;

          light = Math.max(
            0.35,
            Math.min(1.15, light)
          );

          // Slightly compress contrast so the new fabric
          // remains clearly visible while folds remain natural.
          light =
            0.72 +
            light * 0.43;

          od[i] =
            Math.min(255, td[i] * light);

          od[i + 1] =
            Math.min(255, td[i + 1] * light);

          od[i + 2] =
            Math.min(255, td[i + 2] * light);

          od[i + 3] = 255;
        }

        lc.putImageData(out, 0, 0);

        // Soft edge only — do not blur garment structure.
        ctx.filter = "blur(0.18px)";
        ctx.drawImage(layer, 0, 0);
        ctx.filter = "none";
      }
    } catch (e) {
      console.error(e);
      setErr(`Image load aagala: ${e && e.message ? e.message : "unknown"}`);
    }
  }, [photo, maskUrl, sel]);
  useEffect(() => { render(); }, [render]);

  const onDrop = (e) => {
    e.preventDefault(); setOver(false);
    try { const { type, id } = JSON.parse(e.dataTransfer.getData("text/plain")); apply(type, (type === "fabric" ? fabrics : patterns).find((x) => x.id === id)); } catch {}
  };
  const drag = (type, item) => ({ draggable: true, onDragStart: (e) => e.dataTransfer.setData("text/plain", JSON.stringify({ type, id: item.id })), onClick: () => apply(type, item) });
  const save = () => { const a = document.createElement("a"); a.download = "nayara-look.png"; a.href = canvasRef.current.toDataURL("image/png"); a.click(); };

  const part = (r) => [sel[r].fabric?.name, sel[r].pattern?.name].filter(Boolean).join(" + ");
  const look = hasBottom && part("top") !== part("bottom")
    ? `${styleName} · Top: ${part("top") || "White"} · Bottom: ${part("bottom") || "White"}`
    : `${styleName}${part("top") ? ` · ${part("top")}` : ""}`;
  const quote = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(`Hi Nayara! I designed this look in Try Nayara Fab: ${who} – ${look}. Please share a quote.`)}`;
  const railItems = useMemo(() => (fabrics.length > 4 ? [...fabrics, ...fabrics] : fabrics), [fabrics]);
  const price = sel.top.fabric?.price || sel.bottom.fabric?.price || 0;

  return (
    <div data-testid="try-studio" className="min-h-screen bg-[#F6F1E8] px-4 pb-16 pt-8 text-[#1A1410] lg:px-12">
      <style>{CSS}</style>

      {/* header */}
      <div className="mx-auto flex max-w-[1500px] flex-wrap items-end justify-between gap-6">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.42em] text-[#9C7B4C]">Virtual Atelier · Try Nayara Fab</div>
          <h1 className="mt-2 text-4xl font-light sm:text-[46px]" style={SERIF}>Design it. <em className="ts-foil">Drape it.</em> Love it.</h1>
        </div>
        <div className="flex flex-col items-end gap-3">
          <div className="flex gap-1 rounded-full border border-[#e3d7c2] bg-white p-1.5 shadow-[0_8px_20px_rgba(60,40,20,.06)]">
            {MODELS.map(([k, l]) => {
              const th = thumbOf(k === "kids" ? kid : k);
              return (
                <button key={k} onClick={() => setModel(k)} className={`flex items-center gap-2.5 rounded-full py-1.5 pl-1.5 pr-5 text-[12.5px] uppercase tracking-[0.12em] transition ${model === k ? "bg-[#1A1410] text-[#F6F1E8]" : "text-[#6b6157] hover:bg-[#f3eadb]"}`}>
                  <span className="h-12 w-9 overflow-hidden rounded-lg bg-[#efe5d4]">{th && <img src={th} alt="" className="h-full w-full object-cover object-top" />}</span>{l}
                </button>
              );
            })}
          </div>
          {model === "kids" && (
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#9C7B4C]">Kids:
              {["boy", "girl"].map((k) => {
                const th = thumbOf(k);
                return (
                  <button key={k} onClick={() => setKid(k)} className={`flex items-center gap-2 rounded-full border py-1 pl-1 pr-4 tracking-[0.1em] ${kid === k ? "border-[#1A1410] bg-[#1A1410] text-[#F6F1E8]" : "border-dashed border-[#d8c7a6] bg-white text-[#5b5147]"}`}>
                    <span className="h-7 w-7 overflow-hidden rounded-full bg-[#efe5d4]">{th && <img src={th} alt="" className="h-full w-full object-cover object-top" />}</span>{k}
                  </button>
                );
              })}
            </div>
          )}
          <div className="hidden items-center gap-5 text-[11px] uppercase tracking-[0.2em] text-[#9C7B4C] sm:flex">
            {["Choose", "Style", "Drape fabric & pattern"].map((t, i) => (
              <span key={t} className={`flex items-center gap-2 ${i === 0 ? "opacity-50" : "text-[#1A1410]"}`}>
                <b className="grid h-5 w-5 place-items-center rounded-full border border-current text-[10px] font-normal">{i + 1}</b>{t}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* styles */}
      <div className="mx-auto mt-6 flex max-w-[1500px] flex-wrap gap-2.5">
        {STYLES[who].map(([k, l]) => (
          <button key={k} onClick={() => { setStyle(k); setSel(EMPTY); history.current = []; }} className={`rounded-full border px-5 py-2.5 text-[13px] transition ${style === k ? "border-[#1A1410] bg-[#1A1410] text-[#F6F1E8]" : "border-[#e3d7c2] bg-white text-[#5b5147] hover:border-[#B89B72]"}`}>{l}</button>
        ))}
      </div>

      <div className="mx-auto mt-6 grid max-w-[1500px] gap-6 lg:h-[820px] lg:grid-cols-[270px_1fr_270px]">
        {/* fabrics – auto scrolling */}
        <div className="ts-rail flex flex-col overflow-hidden rounded-2xl border border-[#e6dccb] bg-[#FBF8F2] max-lg:h-[340px]">
          <div className="flex items-center justify-between border-b border-[#eee3d1] px-4 py-3.5">
            <b className="text-[26px] font-light" style={SERIF}>Fabrics</b>
            <small className="flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#9C7B4C]"><ChevronsDown className="h-3 w-3" />auto-scroll</small>
          </div>
          <div className="ts-fade relative flex-1 overflow-hidden px-3">
            <div className={fabrics.length > 4 ? "ts-auto" : ""}>
              {railItems.map((f, i) => (
                <div key={`${f.id}-${i}`} {...drag("fabric", f)}
                  className={`mb-3 flex cursor-grab items-center gap-3 rounded-xl border bg-white p-2.5 shadow-[0_4px_12px_rgba(60,40,20,.04)] transition hover:-translate-y-0.5 active:cursor-grabbing ${cur.fabric?.id === f.id ? "border-[#9C7B4C] ring-4 ring-[#C9A96E]/20" : "border-[#eadfcb]"}`}>
                  <img src={imgUrl(f.images?.[0])} alt={f.name} className="h-[70px] w-[70px] flex-none rounded-lg object-cover" draggable={false} />
                  <div className="min-w-0"><h5 className="truncate text-[20px] leading-tight" style={SERIF}>{f.name}</h5>
                    {f.price > 0 && <p className="mt-0.5 text-xs text-[#8a7f72]"><b className="text-sm font-normal text-[#1A1410]">{inr(f.price)}</b> / metre</p>}</div>
                </div>
              ))}
              {!fabrics.length && <p className="py-10 text-center text-sm text-[#8a7f72]">Admin la fabrics add pannunga</p>}
            </div>
          </div>
        </div>

        {/* stage */}
        <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop}
          className="relative overflow-hidden rounded-[22px] border border-[#e6dccb] max-lg:h-[680px]" style={{ background: "radial-gradient(ellipse at 50% 40%,#fffaf2 0%,#efe4d2 70%,#e6d8c1 100%)" }}>
          <div className="absolute left-1/2 top-12 h-[78%] w-[min(470px,80%)] -translate-x-1/2 rounded-t-[240px] border border-[#B89B72]/50" />
          <div className="absolute inset-x-0 bottom-0 h-[22%] bg-gradient-to-b from-[#e3d3b9] to-[#d9c6a7]" />
          <div className="absolute bottom-[19%] left-1/2 h-10 w-[260px] -translate-x-1/2 rounded-[50%] shadow-[0_14px_30px_rgba(60,40,20,.25)]" style={{ background: "radial-gradient(ellipse,#cbb38d,#b89b72)" }} />

          <div className="absolute inset-x-0 bottom-[21%] top-8 flex justify-center">
            {photo ? (
              <motion.canvas key={photo} ref={canvasRef} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                className="h-full w-auto transition-transform duration-500"
                style={{ transform: `scale(${zoom ? 1.3 : 1}) scaleX(${flip ? -1 : 1})`, transformOrigin: "50% 35%", WebkitMaskImage: "linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent)" }} />
            ) : (
              <div className="grid place-items-center px-8 text-center text-sm text-[#8a7f72]">
                {isAdmin ? `"${styleName}" · pose ${pose} model photo upload pannunga (white dress) ↑` : "Model photo coming soon"}
              </div>
            )}
          </div>
          {isAdmin && <ChangeBtn slot={slot} onDone={() => { base.current.key = ""; reload(); }} />}

          {/* dashed drop zone */}
          {photo && (!anyFabric || over) && (
            <div className={`pointer-events-none absolute inset-x-[24%] bottom-[24%] top-[28%] z-10 rounded-[34px] border-2 border-dashed transition ${over ? "border-[#C9A96E] bg-[#F4E4BC]/15 shadow-[0_0_60px_rgba(233,211,161,.6)]" : "border-[#C9A96E]/70"}`}>
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#1A1410] px-3.5 py-1.5 text-[10px] uppercase tracking-[0.22em] text-[#F4E4BC]">✦ Drop fabric on the model</span>
            </div>
          )}

          {/* tools */}
          <div className="absolute left-5 top-5 z-20 flex flex-col gap-3">
            {[[RotateCcw, "Reset to white", reset], [ZoomIn, "Zoom", () => setZoom((z) => !z)], [ArrowLeftRight, "Flip", () => setFlip((f) => !f)], [CornerUpLeft, "Undo", undo]].map(([Ico, t, fn]) => (
              <button key={t} onClick={fn} title={t} className="grid h-11 w-11 place-items-center rounded-full bg-white shadow-md hover:bg-[#f6eedf]"><Ico className="h-4 w-4" /></button>
            ))}
          </div>

          {/* poses */}
          {(POSES.filter(poseHas).length > 1 || isAdmin) && (
            <div className="absolute left-5 top-[236px] z-20 flex flex-col gap-2">
              <small className="text-[9px] uppercase tracking-[0.2em] text-[#9C7B4C]">Pose</small>
              {POSES.filter((n) => poseHas(n) || (isAdmin && (n === 1 || poseHas(n - 1)))).map((n) => (
                <button key={n} onClick={() => { setPose(n); }} title={`Pose ${n}`}
                  className={`h-14 w-10 overflow-hidden rounded-lg border bg-white ${pose === n ? "border-[#1A1410] ring-2 ring-[#C9A96E]/40" : "border-[#e3d7c2]"}`}>
                  {poseHas(n) ? <img src={src(slotFor(style, n), null)} alt="" className="h-full w-full object-cover object-top" /> : <Plus className="mx-auto h-4 w-4 text-[#9C7B4C]" />}
                </button>
              ))}
            </div>
          )}

          <div className="absolute right-5 top-5 z-20 flex flex-col items-end gap-2 text-xs">
            {[["Style", styleName, null], ["Fabric", cur.fabric?.name || "Plain White (default)", cur.fabric], ["Pattern", cur.pattern?.name || "None yet", cur.pattern]].map(([k, v, it]) => (
              <div key={k} className="flex items-center gap-2.5 rounded-full bg-white/90 py-1.5 pl-1.5 pr-4 shadow-sm backdrop-blur">
                <span className="h-7 w-7 overflow-hidden rounded-full border border-[#e3d7c2] bg-white">{it && <img src={imgUrl(it.images?.[0])} alt="" className="h-full w-full object-cover" />}</span>
                <div><small className="block text-[9.5px] uppercase tracking-[0.18em] text-[#9C7B4C]">{k}</small>{v}</div>
              </div>
            ))}
          </div>

          {/* recent looks */}
          {recent.length > 0 && (
            <div className="absolute bottom-[104px] left-5 z-20 rounded-2xl bg-white/95 p-3 shadow-md">
              <small className="block text-[9px] uppercase tracking-[0.2em] text-[#9C7B4C]">Recent looks</small>
              <div className="mt-2 flex gap-2">
                {recent.map(({ type, item }) => (
                  <button key={item.id} onClick={() => apply(type, item)} className="h-11 w-11 overflow-hidden rounded-lg border border-[#e3d7c2]"><img src={imgUrl(item.images?.[0])} alt={item.name} className="h-full w-full object-cover" /></button>
                ))}
              </div>
            </div>
          )}

          {/* top / bottom / full outfit */}          <div className="absolute bottom-[104px] left-1/2 z-20 flex -translate-x-1/2 gap-1 rounded-full bg-white/95 p-1 shadow-md">
            {TARGETS.map(([k, l]) => (
              <button
                key={k}
                onClick={() => setTarget(k)}
                className={`rounded-full px-4 py-1.5 text-[11px] uppercase tracking-[0.14em] transition ${
                  target === k
                    ? "bg-[#1A1410] text-[#F6F1E8]"
                    : "text-[#6b6157] hover:bg-[#f3eadb]"
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          {!anyFabric && photo && (
            <div className="absolute bottom-[26%] left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full bg-white/90 px-4 py-2 text-[11.5px] text-[#5b5147]">
              <Hand className="h-3.5 w-3.5 text-[#9C7B4C]" />Drag or tap a fabric / pattern to drape it
            </div>
          )}
          {err && <div className="absolute left-1/2 top-20 z-20 -translate-x-1/2 rounded-lg bg-[#a8382f] px-4 py-2 text-xs text-white">{err}</div>}

          <div className="absolute inset-x-5 bottom-5 z-20 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#1A1410] px-5 py-3.5 text-[#F6F1E8]">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#C9A96E]">Your look</div>
              <div className="truncate text-[21px]" style={SERIF}>{anyFabric ? look : <>{styleName} · <em className="text-[#E9D3A1]">White canvas</em> — drop a fabric to begin</>}
                {price > 0 && <span className="ml-2 font-sans text-[13px] text-[#C9A96E]">≈ {inr(price)}/m</span>}</div>
            </div>
            <div className="flex gap-2.5">
              <button onClick={save} disabled={!photo} className="flex items-center gap-2 whitespace-nowrap rounded-xl border border-[#C9A96E]/50 px-4 py-3 text-xs uppercase tracking-[0.14em] text-[#F4E4BC] disabled:opacity-40"><Download className="h-4 w-4" />Save look</button>
              <a href={quote} target="_blank" rel="noreferrer" className="flex items-center gap-2 whitespace-nowrap rounded-xl px-5 py-3 text-xs uppercase tracking-[0.16em] text-[#1A1410]" style={{ background: FOIL }}>Get a quote <ArrowRight className="h-4 w-4" /></a>
            </div>
          </div>
        </div>

        {/* patterns – manual scroll */}
        <div className="flex flex-col overflow-hidden rounded-2xl border border-[#e6dccb] bg-[#FBF8F2] max-lg:h-[340px]">
          <div className="flex items-center justify-between border-b border-[#eee3d1] px-4 py-3.5">
            <b className="text-[26px] font-light" style={SERIF}>Patterns</b>
            <small className="text-[10px] uppercase tracking-[0.2em] text-[#9C7B4C]">scroll</small>
          </div>
          <div className="ts-scroll flex-1 overflow-y-auto p-3">
            {patterns.map((p) => (
              <div key={p.id} {...drag("pattern", p)}
                className={`relative mb-3 h-[118px] cursor-grab overflow-hidden rounded-xl border bg-cover bg-center transition hover:-translate-y-0.5 ${cur.pattern?.id === p.id ? "border-[#9C7B4C] outline outline-2 outline-offset-2 outline-[#9C7B4C]" : "border-[#eadfcb]"}`}
                style={{ backgroundImage: `url(${imgUrl(p.images?.[0])})` }}>
                {cur.pattern?.id === p.id && <span className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full bg-[#1A1410]"><Check className="h-3.5 w-3.5 text-[#F4E4BC]" /></span>}
                <div className="absolute inset-x-2 bottom-2 flex items-center justify-between rounded-lg bg-white/90 px-2.5 py-1.5 text-[17px]" style={SERIF}>
                  <span className="truncate">{p.name}</span>{p.price > 0 && <small className="font-sans text-[11px] text-[#9C7B4C]">{inr(p.price)}</small>}
                </div>
              </div>
            ))}
            {!patterns.length && <p className="py-10 text-center text-sm text-[#8a7f72]">Admin la patterns add pannunga</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
